import { useMemo } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

interface WeatherMapProps {
  latitude: number;
  longitude: number;
  mapType: 'temperature' | 'precipitation';
  isDarkMode?: boolean;
  height?: number;
}

// Build WeatherAPI tile URL based on current UTC date/hour
const getWeatherTileUrl = (mapType: WeatherMapProps['mapType']) => {
  const now = new Date();
  const dateStr = now.toISOString().slice(0, 10).replace(/-/g, ''); // yyyyMMdd
  const hourStr = String(now.getUTCHours()).padStart(2, '0'); // HH (UTC)

  const typeMap: Record<WeatherMapProps['mapType'], string> = {
    precipitation: 'precip',
    temperature: 'tmp2m',
  };

  const layer = typeMap[mapType] || 'precip';
  return `https://weathermaps.weatherapi.com/${layer}/tiles/${dateStr}${hourStr}/{z}/{x}/{y}.png`;
};

export function WeatherMap({ latitude, longitude, mapType, isDarkMode = false, height = 240 }: WeatherMapProps) {
  const weatherTileUrl = useMemo(() => getWeatherTileUrl(mapType), [mapType]);

  const baseTileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  return (
    <div className="rounded-sm overflow-hidden border relative z-0" style={{ height }}>
      <MapContainer
        center={[latitude, longitude]}
        zoom={6}      // zoomed out for wider context
        minZoom={3}   // don't go too far out
        maxZoom={12}  // allow closer inspection
        scrollWheelZoom={false}
        zoomControl={true}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        className={isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}
      >
        <TileLayer
          url={baseTileUrl}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <TileLayer
          url={weatherTileUrl}
          opacity={0.6}
          attribution='Weather tiles &copy; WeatherAPI.com'
        />
      </MapContainer>
    </div>
  );
}

